import type { RouteObject } from "react-router";
import { Navigate } from "react-router-dom";
import Login from "../pages/Auth/Login";
import AuthCallback from "../pages/Auth/Callback";
import AuthComplete from "../pages/Auth/Complete";
import ForgotPassword from "../pages/Auth/ForgotPassword";
import ResetPassword from "../pages/Auth/ResetPassword";
import Register from "../pages/Register";
import ActivateAccount from "../pages/ActivateAccount";
import PortfolioPage from "../pages/PortfolioPage";
import PublicPortfolioPage from "../pages/PublicPortfolioPage";
import PublicResumePage from "../pages/PublicResumePage";
import PublicResumePdfPage from "../pages/PublicResumePdfPage";
import ResumePage from "../pages/ResumePage";
import LandingPage from "../pages/Landing";
import OnboardingPage from "../pages/Onboarding";
import AgentPage from "../pages/Agent";
import AiAutomationPage from "../pages/AiAutomation";
import CaseStudiesPage from "../pages/CaseStudies";
import CaseStudyView from "../pages/CaseStudies/CaseStudyView";
import StackPage from "../pages/Stack";
import LeadsPage from "../pages/Leads";
import UserAccessPage from "../pages/UserAccess";
import AuthRedirect from "../components/auth/AuthRedirect";
import RequireAuth from "../components/auth/RequireAuth";
import RequireLeadAccess from "../components/auth/RequireLeadAccess";
import RequireSuperAdmin from "../components/auth/RequireSuperAdmin";
import AccentSidebarLayout from "../layouts/AccentSidebarLayout";
import CrmGateway from "../pages/CrmGateway";
import LmsGateway from "../pages/LmsGateway";
import CommerceGateway from "../pages/CommerceGateway";
import AgentOpsGateway from "../pages/AgentOpsGateway";
import LangChatGateway from "../pages/LangChatGateway";
import { AI_AUTOMATION_PATH, ONBOARD_PATH } from "../utils/navigation";

function aiAutomationRoute() {
  return {
    element: <AccentSidebarLayout />,
    children: [
      {
        path: "",
        element: <AiAutomationPage />,
      },
    ],
  };
}

type RouterConfig = {
  token: string | null;
  onLogout: () => void;
};

export default function createRouter({
  token,
  onLogout,
}: RouterConfig): RouteObject[] {
  return [
    {
      path: "/login",
      element: token ? <AuthRedirect /> : <Login />,
    },
    {
      path: "/auth/callback",
      element: <AuthCallback />,
    },
    {
      path: "/auth/complete",
      element: <AuthComplete />,
    },
    {
      path: "/forgot-password",
      element: token ? <Navigate to="/" replace /> : <ForgotPassword />,
    },
    {
      path: "/register",
      element: token ? <Navigate to="/" replace /> : <Register />,
    },
    {
      path: "/reset-password",
      element: <ResetPassword />,
    },
    {
      path: "/activate-account",
      element: <ActivateAccount />,
    },
    {
      path: "/onboard",
      element: <Navigate to={ONBOARD_PATH} replace />,
    },
    {
      path: "/onboarding",
      element: <Navigate to={ONBOARD_PATH} replace />,
    },
    {
      path: "/",
      element: <Navigate to={AI_AUTOMATION_PATH} replace />,
    },
    {
      path: "/portfolio",
      element: token ? <Navigate to="/accent-sidebar/portfolio" replace /> : <Navigate to="/login" replace />,
    },
    // Registered ahead of the "/:username" public profile catch-all below.
    {
      path: "/ai-automation",
      ...aiAutomationRoute(),
    },
    {
      path: "/profiles",
      element: <AccentSidebarLayout />,
      children: [{ path: "", element: <LandingPage /> }],
    },
    {
      path: "/case-studies",
      element: <AccentSidebarLayout />,
      children: [
        { path: "", element: <CaseStudiesPage /> },
        { path: ":slug", element: <CaseStudyView /> },
      ],
    },
    {
      path: "/stack",
      element: <AccentSidebarLayout />,
      children: [{ path: "", element: <StackPage /> }],
    },
    {
      path: "/Deo_Bernal_Resume.pdf",
      element: <AccentSidebarLayout />,
      children: [
        {
          path: "",
          element: <PublicResumePdfPage />,
        },
      ],
    },
    {
      path: "/accent-sidebar",
      element: (
        <RequireAuth>
          <AccentSidebarLayout />
        </RequireAuth>
      ),
      children: [
        {
          path: "",
          element: <Navigate to="portfolio" replace />,
        },
        {
          path: "portfolio",
          element: <PortfolioPage onLogout={onLogout} />,
        },
        {
          path: "resume",
          element: <ResumePage />,
        },
        {
          path: "onboarding",
          element: <OnboardingPage />,
        },
        {
          path: "agent",
          element: <AgentPage />,
        },
        {
          path: "leads",
          element: (
            <RequireLeadAccess>
              <LeadsPage />
            </RequireLeadAccess>
          ),
        },
        {
          path: "access",
          element: (
            <RequireSuperAdmin>
              <UserAccessPage />
            </RequireSuperAdmin>
          ),
        },
      ],
    },
    {
      path: "/crm/*",
      element: <CrmGateway />,
    },
    {
      path: "/lms/*",
      element: <LmsGateway />,
    },
    {
      path: "/commerce/*",
      element: <CommerceGateway />,
    },
    {
      path: "/agent/*",
      element: <AgentOpsGateway />,
    },
    {
      path: "/langchat/*",
      element: <LangChatGateway />,
    },
    {
      path: "/:username",
      element: <AccentSidebarLayout />,
      children: [
        {
          path: "",
          element: <PublicPortfolioPage />,
        },
        {
          path: "resume",
          element: <PublicResumePage />,
        },
      ],
    },
    {
      path: "*",
      element: <Navigate to={token ? "/accent-sidebar" : AI_AUTOMATION_PATH} replace />,
    },
  ];
}